import { useMemo, useCallback } from "react";

const withMsg = (id, msg) => ({ id, msg });

/**
 * Validate service-scheduling configs.
 *
 * @param {Array} configs   – Configs to validate
 * @param {Array} services  – Master list of services
 * @param {Object} opts     – { checkCoverage?: boolean } (default true)
 *
 * Rules
 * ─────────────────────────────────────────────────────────────
 * ① A service cannot appear in > 1 config
 * ② Non-“apply-to-all” configs must select ≥ 1 service
 * ③ Employee-managed numeric fields (> 0)
 * ④ Business-managed uploadEveryNDays (> 6)
 * ⑤ Exactly one “apply-to-all”, and it must be the only config
 * ⑥ Config count ≤ service count
 * ⑦ (opt) Every service covered unless an “apply-to-all” exists
 */
export default function useServiceConfigsValidation(
  configs = [],
  services = [],
  opts = {}
) {
  const { checkCoverage = true } = opts;

  const validateAll = useCallback(() => {
    const next = [];
    if (!Array.isArray(configs) || !Array.isArray(services)) return next;

    let hasApplyAll = false;
    let applyAllCount = 0;
    const svcSeen = {};

    /* ───── per-config checks ───── */
    configs.forEach((c) => {
      /* track apply-all */
      if (c.applyToAll) {
        hasApplyAll = true;
        applyAllCount += 1;
      }

      /* ① duplicate service assignment */
      (c.services || []).forEach((id) => {
        if (svcSeen[id]) next.push(withMsg(c.id, "Duplicate service assignment"));
        svcSeen[id] = true;
      });

      /* ② must select ≥ 1 service if not apply-to-all */
      if (!c.applyToAll && (!(c.services || []).length)) {
        next.push(withMsg(c.id, "Select at least one service"));
      }

      /* ③ employee-managed numeric checks */
      if (c.mode === "EMPLOYEE_MANAGED") {
        const bw = c.employeeManagedConfig?.bookingWindowWeeks ?? 0;
        const mc = c.employeeManagedConfig?.maxConcurrent ?? 0;
        if (bw <= 0) next.push(withMsg(c.id, "Booking window must be greater than 0"));
        if (mc <= 0) next.push(withMsg(c.id, "Max concurrent must be greater than 0"));
      }

      /* ④ business-managed numeric check */
      if (c.mode === "BUSINESS_MANAGED") {
        const days = c.businessManagedConfig?.uploadEveryNDays ?? 0;
        if (days <= 6) {
          next.push(withMsg(c.id, "Upload every N days must be greater than 6"));
        }
      }
    });

    /* ⑤ cannot mix or duplicate apply-to-all */
    if (hasApplyAll && (configs.length > 1 || applyAllCount > 1)) {
      configs.forEach((c) => {
        next.push(
          withMsg(
            c.id,
            c.applyToAll
              ? "“Apply to all” must be the only config."
              : "Cannot mix “apply to all” with other configs."
          )
        );
      });
    }

    /* ⑥ more configs than services? */
    if (configs.length > services.length) {
      next.push(
        withMsg("*", `You can have at most ${services.length} configs.`)
      );
    }

    /* ⑦ optional coverage check */
    if (checkCoverage && !hasApplyAll) {
      const missing = services.filter((s) => !svcSeen[s.id]);
      if (missing.length) {
        const names = missing.map((s) => s.name || s.id).join(", ");
        next.push(
          withMsg(
            "*",
            `Unassigned services: ${names}. Attach them to a config or enable “Apply to all”.`
          )
      ); 
      }
    }

    return next;
  }, [configs, services, checkCoverage]);

  /* compute once per render */
  const errors = useMemo(() => validateAll(), [validateAll]);

  return { errors, validateAll };
}
